import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";

import useWorkspace from "../workspaces/useWorkspace";
import usePagePermissions from "../../../hooks/usePagePermissions";
import PageAPI from "../../api/PageAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { ViewTypesWithField } from "../../../components/PageView/AddViewDrawer/AddViewDrawer";
import useCloudPermissions from "../../../hooks/useCloudPermissions";

/**
 * For adding Page Views AND View SubViews.
 */
const useAddView = (pageID: string) => {
  const { workspace } = useWorkspace();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantID }: any = useParams();
  const { pPermissions } = usePagePermissions(pageID);
  const { cPermissions } = useCloudPermissions();

  const addView = useMutation({
    mutationFn: ({
      payload,
      onSuccess,
      parentViewID, // Optional, the view to save this view under (for subViews)
    }: {
      payload: any;
      onSuccess?: () => void;
      parentViewID?: string;
    }) => {
      if (!pPermissions.page_create_view && !cPermissions.cloud_create_page) {
        return Promise.reject("You do not have permission to create a view.");
      }

      return PageAPI.createView(
        workspace.id,
        pageID,
        payload,
        parentViewID
      ).then((data: any) => {
        const { page, view, subViewID } = data.data.data;

        page && queryClient.setQueryData(["page", pageID], page);
        view && queryClient.setQueryData(["view", view._id], view);

        // Don't redirect for external link.
        if (payload?.Type === "External") {
          return callFunctionIfExists(onSuccess);
        }

        // Redirect automatically if it's not a subView
        if (!parentViewID) {
          navigate(
            `/clouds/${tenantID}/workspaces/${workspace.id}/pages/${pageID}/views/${view._id}`
          );
        }

        let finalViewID = subViewID || view._id;

        // A different Tab ID is needed for Subitem, RichText, and Gallery SubViews.
        if (subViewID) {
          const subView = view?.SubViews?.find?.(
            (v: any) => v?.id === subViewID
          );

          if (ViewTypesWithField.includes(subView?.Type)) {
            finalViewID = `${subView?.Type}-${subView?.FieldAPIName}-${finalViewID}`;
          }
          if (subView?.ReusableView === true) {
            finalViewID = `ReuseView-${finalViewID}`;
          }
        }

        callFunctionIfExists(onSuccess, finalViewID);
      });
    },
  });

  return {
    addView: addView.mutate,
    viewAdding: addView.isPending,
    addViewError: addView.error ? getErrorMessage(addView.error) : null,
  };
};

export default useAddView;
